import {
  PatchTaskRequest,
  PostTaskRequest,
} from "@fartherfinance/frontend/api/TaskWorkflows/Types";

import { FormData } from "../../core/types";

import { CreateFieldMappings, UpdateFieldMappings } from "./apiMapperTypes";
import {
  mapFormDataToApiFields,
  validateRequiredFields,
} from "./genericApiMapper";

/**
 * Maps form data to PostTaskRequest format for creating new tasks
 * @param formData - Form data from FormEngine
 * @param templateId - Template ID from current template context
 * @param parentId - Optional parent task ID for subtasks
 * @returns PostTaskRequest formatted for the TaskWorkflows API
 */
export const mapFormDataToTaskRequest = (
  formData: FormData,
  templateId: string,
  parentId?: string
): PostTaskRequest => {
  // Validate required fields
  const missingFields = validateRequiredFields(formData, CreateFieldMappings);
  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
  }

  // Map fields using the declarative configuration
  const fields = mapFormDataToApiFields(formData, CreateFieldMappings);

  return {
    template: {
      id: templateId,
    },
    parentId: parentId || null,
    fields: fields as PostTaskRequest["fields"],
  };
};

/**
 * Maps form data to PatchTaskRequest format for selective updates (only dirty fields)
 * @param formData - Current form data from FormEngine
 * @param dirtyFields - Object containing only the fields that have changed (from formState.dirtyFields)
 * @param templateId - Template ID from current template context
 * @returns PatchTaskRequest formatted for the TaskWorkflows API with only dirty fields
 */
export const mapFormDataToUpdateRequest = (
  formData: FormData,
  dirtyFields: Record<string, any>,
  templateId: string
): PatchTaskRequest => {
  // Create a subset of form data containing only dirty fields
  const dirtyFormData: FormData = {};

  Object.keys(dirtyFields).forEach((fieldKey) => {
    if (fieldKey in formData) {
      dirtyFormData[fieldKey] = formData[fieldKey];
    }
  });

  // Map only the dirty fields using the update configuration
  const fields = mapFormDataToApiFields(dirtyFormData, UpdateFieldMappings);

  return {
    template: {
      id: templateId,
    },
    fields: fields as Partial<PatchTaskRequest["fields"]>,
  };
};
